import TextField from '@mui/material/TextField';
import {
  MobileStateQuestion,
  SaveDataAnswerGroup,
  SaveDataAnswerType,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import { useEffect, useMemo, useState } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import { Box, Typography, Button } from '@mui/material';
import { Platform } from '../../constants/platform';
import { PrepPopModal } from '../../components/Questions/PrepPopModal';
import {
  confirmData,
  QuestionConfirmData,
} from '../../components/Questions/QuestionConfirmData';

interface CustomTextFieldProps {
  question: MobileStateQuestion;
  answers: SaveDataAnswerGroup | undefined;
  disabled?: boolean;
  stringMapper: (key: string) => string;
  onSetAnswer: (questionKey: string, answers: string[]) => void;
  showTitle?: boolean;
}

const CustomTextField = ({
  question,
  answers,
  disabled,
  onSetAnswer,
  stringMapper,
  showTitle,
}: CustomTextFieldProps) => {
  const [userAnswer, setUserAnswer] = useState<string>('');

  const [confirmAnswer, setConfirmAnswer] = useState<string | undefined>();
  const setAnswer = (newanswer: string) => {
    if (answers?.AnswerType === SaveDataAnswerType.PrePop) {
      setConfirmAnswer(newanswer);
    } else {
      setUserAnswer(newanswer);
      if (onSetAnswer) onSetAnswer(question.QuestionKey, [newanswer]);
    }
  };

  const label = useMemo(() => {
    if (!showTitle) {
      return '';
    }
    if (stringMapper) {
      return stringMapper(question?.Title);
    }
    return question?.Title;
  }, [showTitle, question, stringMapper]);

  useEffect(() => {
    const answerActive =
      answers?.Answers?.find((answer) => answer.Active)?.Answer ?? undefined;
    if (answerActive !== undefined) {
      setUserAnswer(answerActive);
    }
  }, [answers]);

  const validateInput = () => {
    if (userAnswer) {
      setAnswer(userAnswer);
    } else {
      setAnswer('');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1',
      }}
    >
      <TextField
        label={label}
        variant='outlined'
        fullWidth
        value={userAnswer}
        disabled={disabled}
        multiline={question?.Type === 'MultiLineText'}
        rows={question?.Type === 'MultiLineText' ? 4 : undefined}
        onChange={(event) => {
          let inputValue = event.target.value;
          if (question.Type === 'Numeric' || question.Type === 'Currency') {
            // Allow only digits (and optionally a single decimal point)
            inputValue = inputValue.replaceAll(/[^\d.]/g, '');
            // Optional: Only allow one decimal point
            const parts = inputValue.split('.');
            if (parts.length > 2) {
              inputValue = parts[0] + '.' + parts.slice(1).join('');
            }
          }

          setUserAnswer(inputValue);
        }}
        onBlur={validateInput}
        type={
          question.Type === 'Numeric' || question.Type === 'Currency'
            ? 'number'
            : question.Type === 'Phone'
              ? 'tel'
              : 'text'
        }
        inputMode={
          question.Type === 'Phone'
            ? 'tel'
            : question.Type === 'Numeric' || question.Type === 'Currency'
              ? 'numeric'
              : undefined
        }
        InputProps={{
          startAdornment:
            question?.Type === 'Currency' ? (
              <InputAdornment position='start'>$</InputAdornment>
            ) : null,
        }}
      />

      {confirmAnswer && (
        <PrepPopModal
          visible={confirmAnswer !== undefined}
          hide={() => {
            setConfirmAnswer(undefined);
          }}
        >
          <Typography variant='h6' sx={{ mb: 1 }}>
            {' '}
            {confirmData?.title} from {answers?.Answers[0]?.Answer ?? ''} to{' '}
            {confirmAnswer}
          </Typography>
          <Typography variant='body1' sx={{ mb: 2 }}>
            {' '}
            {confirmData?.message}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant='outlined'
              fullWidth
              onClick={() => {
                setConfirmAnswer(undefined);
                if (answers !== undefined && answers?.Answers !== undefined) {
                  setUserAnswer(answers?.Answers[0]?.Answer ?? '');
                }
              }}
            >
              {confirmData?.cancelText}
            </Button>
            <Button
              variant='contained'
              fullWidth
              onClick={() => {
                if (confirmAnswer !== undefined && onSetAnswer) {
                  onSetAnswer(question.QuestionKey, [confirmAnswer]);
                  setConfirmAnswer(undefined);
                }
              }}
            >
              {confirmData?.successText}
            </Button>
          </Box>
        </PrepPopModal>
      )}
    </Box>
  );
};

export default CustomTextField;
